import _ from 'lodash'

import { utils } from 'frontend-js-shared'

import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'

/**
 * Builds the render shape for checklist reply items.
 *
 * Responsibilities, in order:
 * 1. Index: stamp every raw item with `originalIndex`, its position in the
 *    raw `checklist.items` array. Event handlers use it to route changes
 *    back to the raw item, so it must be assigned before any filtering.
 * 2. Filter: drop items hidden by the visibility evaluation (pass
 *    `visibilityById` as null to keep everything, e.g. the unfiltered
 *    `scoringItems` pass).
 * 3. Decorate: flag finding-eligible options, gated by `allowFindings`.
 * 4. Group: nest the items that follow a separator into that separator's
 *    `items` array; ungrouped items stay top level.
 *
 * Pure: no component state, no side effects.
 */
export function buildRenderItems(
  sourceItems,
  { visibilityById = null, allowFindings = false } = {}
) {
  return groupUnderSections(
    indexAndFilterVisible(sourceItems, visibilityById),
    allowFindings
  )
}

function indexAndFilterVisible(sourceItems, visibilityById) {
  return sourceItems
    .map((item, index) => ({ ...item, sourceIndex: index }))
    .filter(
      item =>
        !visibilityById || visibilityById.get(utils.getEntityId(item)) !== false
    )
}

function buildRenderItem(item, allowFindings) {
  return {
    ...item,
    originalIndex: item.sourceIndex,
    hasOptionRequiringFinding: isFindingEligible(item, allowFindings),
  }
}

function isFindingEligible(item, allowFindings) {
  return (
    Boolean(allowFindings) &&
    Boolean(item?.options?.some(option => option.requiresFinding))
  )
}

function groupUnderSections(indexedItems, allowFindings) {
  return indexedItems.reduce((result, item) => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      result.push({
        ...item,
        originalIndex: item.sourceIndex,
        items: [],
      })
      return result
    }

    const lastItem = _.last(result)
    if (lastItem?.type === ITEM_TYPES.SEPARATOR) {
      lastItem.items.push(buildRenderItem(item, allowFindings))
      return result
    }

    result.push(buildRenderItem(item, allowFindings))
    return result
  }, [])
}
